// Phase 3.5 — Pricing guardrails: GP warning, multiplier cap display,
// "use suggested by default", "exclude canned from learning".
import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Loader2, Save, RotateCcw } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import {
  useShopPricingSettings,
  useUpsertPricingSettings,
} from '@/hooks/pricing/useShopPricingConfig';
import { useResetMatrixToDefault } from '@/hooks/pricing/useResetMatrixToDefault';
import { SYSTEM_MAX_MULTIPLIER } from '@/lib/parts-financial/calculator';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

interface Props { shopId: string; }

export function MatrixGuardrailsSection({ shopId }: Props) {
  const settingsQ = useShopPricingSettings(shopId);
  const upsert = useUpsertPricingSettings();
  const reset = useResetMatrixToDefault();

  const [minGp, setMinGp] = useState(30);
  const [useSuggested, setUseSuggested] = useState(true);
  const [excludeCanned, setExcludeCanned] = useState(true);
  const [globalMaxMult, setGlobalMaxMult] = useState(5);

  useEffect(() => {
    if (!settingsQ.data) return;
    setMinGp(Number(settingsQ.data.min_gp_warning_pct ?? 30));
    setUseSuggested(settingsQ.data.use_suggested_by_default ?? true);
    setExcludeCanned(settingsQ.data.exclude_canned_from_learning ?? true);
    setGlobalMaxMult(Math.min(Number(settingsQ.data.global_max_multiplier ?? 5), SYSTEM_MAX_MULTIPLIER));
  }, [settingsQ.data]);

  const handleSave = async () => {
    if (globalMaxMult <= 0 || globalMaxMult > SYSTEM_MAX_MULTIPLIER) {
      toast({
        title: `Global multiplier cap must be 0 < x ≤ ${SYSTEM_MAX_MULTIPLIER}`,
        variant: 'destructive',
      });
      return;
    }
    try {
      await upsert.mutateAsync({
        shop_id: shopId,
        min_gp_warning_pct: minGp,
        use_suggested_by_default: useSuggested,
        exclude_canned_from_learning: excludeCanned,
        global_max_multiplier: globalMaxMult,
      } as any);
      toast({ title: 'Guardrails saved' });
    } catch (e: any) {
      toast({ title: 'Save failed', description: e?.message, variant: 'destructive' });
    }
  };

  const handleReset = async () => {
    try {
      const r = await reset.mutateAsync(shopId);
      toast({ title: `Default matrix restored (${r.inserted} tiers)` });
    } catch (e: any) {
      toast({ title: 'Reset failed', description: e?.message, variant: 'destructive' });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Guardrails & Defaults</CardTitle>
        <CardDescription>
          Safety thresholds and shop defaults. The system enforces a hard multiplier cap of {SYSTEM_MAX_MULTIPLIER}× regardless of these values.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Minimum GP warning threshold (%)</Label>
            <Input
              type="number" min="0" max="99" step="1"
              value={minGp}
              onChange={(e) => setMinGp(Number(e.target.value))}
            />
            <p className="text-xs text-muted-foreground">
              Parts priced below this GP show a warning. Does not block the price.
            </p>
          </div>
          <div className="space-y-2">
            <Label>Global max multiplier</Label>
            <Input
              type="number" min="1" max={SYSTEM_MAX_MULTIPLIER} step="0.1"
              value={globalMaxMult}
              onChange={(e) => setGlobalMaxMult(Number(e.target.value))}
            />
            <p className="text-xs text-muted-foreground">
              Hard ceiling on any matrix multiplier. System maximum is {SYSTEM_MAX_MULTIPLIER}×.
            </p>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-start gap-3 rounded-md border p-3">
            <Switch
              id="use-suggested-default"
              checked={useSuggested}
              onCheckedChange={setUseSuggested}
            />
            <div className="space-y-0.5">
              <Label htmlFor="use-suggested-default" className="cursor-pointer">
                Use suggested pricing by default for new parts
              </Label>
              <p className="text-xs text-muted-foreground">
                When ON, new parts lines pre-fill the actual sell with the matrix suggestion.
                Manual edits remain authoritative — suggestions never auto-overwrite a locked price.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3 rounded-md border p-3">
            <Switch
              id="exclude-canned"
              checked={excludeCanned}
              onCheckedChange={setExcludeCanned}
            />
            <div className="space-y-0.5">
              <Label htmlFor="exclude-canned" className="cursor-pointer">
                Exclude canned jobs from learning & analytics
              </Label>
              <p className="text-xs text-muted-foreground">
                Templated job parts won't pollute future pricing intelligence.
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-col-reverse md:flex-row md:items-center md:justify-between gap-2 pt-2">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm" disabled={reset.isPending}>
                {reset.isPending
                  ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  : <RotateCcw className="h-4 w-4 mr-2" />}
                Reset to OptiBay default matrix
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Reset default matrix?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will replace your default pricing matrix tiers with the canonical
                  9-tier OptiBay default. Category-specific matrices and existing parts
                  lines are not affected.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleReset}>Reset matrix</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <Button onClick={handleSave} disabled={upsert.isPending} size="sm">
            {upsert.isPending
              ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              : <Save className="h-4 w-4 mr-2" />}
            Save guardrails
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
